import type { JSX, ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageTitle, Tabs, type TabItem } from '../ds';
import type { MyPlanCardDto } from '../../lib/api/openingTasks';
import '../../pages/MyTasksOpening.css';
import './OpeningPlan.css';
import { PlanAppealsTab } from './PlanAppealsTab';
import { PlanChecklistsTab } from './PlanChecklistsTab';
import { PlanGantt } from './PlanGantt';
import { PlanHeader } from './PlanHeader';
import { PlanOverviewTab } from './PlanOverviewTab';
import { PlanTasksTab } from './PlanTasksTab';
import {
  openPartnerTaskCount,
  paramsForStage,
  paramsForTab,
  paramsWithoutStage,
  PLAN_COPY,
  tabFromParams,
  type PlanMode,
  type PlanTab,
} from './plan-model';

/**
 * Визард «Открытие точки» (владелец 31.10.2026) — то, что у куратора в CRM на карточке открытия, для партнёра и
 * только для чтения (кроме отметок своих задач): шапка с датами и вкладки «Обзор», «Диаграмма Ганта», «Задачи»,
 * «Чеклисты», «Обращения». Вкладка живёт в адресе (`?tab=`, «Обзор» — без параметра); этап для задач — `?stage=`,
 * помещение в «Обращениях» — `?room=`. Заявка на переход на следующий этап — только в «Моих задачах».
 *
 * `mode="manager"` — тот же план для менеджера УК (право «Этапы открытия — просмотр»): всё только для чтения, без
 * «Моих задач», траектории и переписки по помещениям (она у партнёра). `card` / `status` из адреса при смене вкладки
 * и этапа сохраняются. `crumbs` — строка над заголовком («← Все открытия»).
 */
export function OpeningPlanView({ card, mode = 'partner', crumbs }: { card: MyPlanCardDto; mode?: PlanMode; crumbs?: ReactNode }): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const tab = tabFromParams(sp);
  const tabsRef = useRef<HTMLDivElement>(null);

  // На телефоне пять вкладок не помещаются: открытая по ссылке (например, «Обращения») должна быть видна в полосе.
  useEffect(() => {
    const bar = tabsRef.current?.querySelector<HTMLElement>('.fk-ds-tabs');
    const active = bar?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!bar || !active) return;
    bar.scrollLeft = Math.max(0, active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2);
  }, [tab]);

  const items = useMemo<TabItem<PlanTab>[]>(
    () => [
      { key: 'overview', label: 'Обзор' },
      { key: 'gantt', label: 'Диаграмма Ганта' },
      { key: 'tasks', label: 'Задачи', badge: openPartnerTaskCount(card.tasks), badgeColor: 'var(--fk-orange)' },
      { key: 'checklists', label: 'Чеклисты' },
      // Осмотры помещений тоже заявки: число — всё, что подано по этой точке.
      { key: 'appeals', label: 'Обращения', badge: card.submissions.length },
    ],
    [card.tasks, card.submissions],
  );

  const goTab = (next: PlanTab): void => {
    if (next !== tab) setSp(paramsForTab(sp, next));
  };
  const goStage = (code: string): void => setSp(paramsForStage(code, sp));
  const clearStage = (): void => setSp(paramsWithoutStage(sp));

  return (
    <div className="fk-op">
      <PageTitle title="Открытие точки" sub={PLAN_COPY[mode].sub} crumbs={crumbs} style={{ marginBottom: 0 }} />
      <PlanHeader card={card} />
      <div ref={tabsRef} style={{ minWidth: 0 }}>
        <Tabs<PlanTab> items={items} value={tab} onChange={goTab} />
      </div>
      <div role="tabpanel" style={{ minWidth: 0 }}>
        {tab === 'overview' ? <PlanOverviewTab card={card} mode={mode} onGoStage={goStage} /> : null}
        {tab === 'gantt' ? <PlanGantt card={card} /> : null}
        {tab === 'tasks' ? <PlanTasksTab card={card} mode={mode} stageParam={sp.get('stage')} onClearStage={clearStage} /> : null}
        {tab === 'checklists' ? <PlanChecklistsTab card={card} mode={mode} /> : null}
        {tab === 'appeals' ? <PlanAppealsTab card={card} mode={mode} /> : null}
      </div>
    </div>
  );
}
